import forge from 'node-forge'
import { URL_CRYPTO_KEY_LIST } from '../constants/urls'
import trim from 'lodash/trim'
import { unwrapKey as unwrapKeyRSA } from './rsa'
import { mergeMapAwait, base64encode, base64decode } from './util'
import cache from './cache'
import isEqual from 'lodash/isEqual'
import debug from '../core/debug'

const IV_LENGTH = 16 // bytes
export const KEY_SIZE = 32 // bytes

export function generateKey () {
  return Promise.resolve(forge.random.getBytes(KEY_SIZE))
}

function unwrapKey (key) {
  return !key || key.length === KEY_SIZE
    ? Promise.resolve(key)
    : unwrapKeyRSA(key)
}

export function fetchKeyUrls (count = 2) {
  return fetch(URL_CRYPTO_KEY_LIST + `?count=${count}`, { credentials: 'same-origin', headers: { 'Cookie': self.cookie } })
    .then(resp => resp.json())
}

function fetchKeys () {
  return fetchKeyUrls()
    .then(
      ({ data }) => mergeMapAwait(data, ({ uri }) => fetchKey(uri), 'raw')
    )
    .then(
      list => mergeMapAwait(list, ({ raw }) => unwrapKey(raw), 'key')
    )
    .catch(e => {
      debug.warn(`Unable to list encryption keys. ${e.message}`)
      return []
    })
}

export function listKeys () {
  return cache('listKeys', fetchKeys)
}

export function fetchKey (url) {
  return fetch(url, { credentials: 'same-origin', headers: { 'Cookie': self.cookie } })
    .then(resp => resp.text())
    .then(trim)
    .then(base64decode)
    .catch(e => null)
}

function decryptWithKey (key, tuple) {
  const iv = tuple.slice(0, IV_LENGTH)
  const encrypted = tuple.slice(IV_LENGTH)
  const input = forge.util.createBuffer(encrypted)
  const cipher = forge.aes.startDecrypting(key, iv)
  cipher.update(input)
  const status = cipher.finish()

  if (status === false) {
    throw Error('Decryption error')
  }

  return cipher.output.data
}

export function decrypt (message, appendKey = true) {
  const decoded = base64decode(message)
  return listKeys()
    .then(
      list =>
        list.reduce(
          (a, { key, uri }) => {
            try {
              const result = decryptWithKey(key, decoded)
              return appendKey
                ? { ...JSON.parse(result), keyUrl: uri }
                : JSON.parse(result)
            } catch (e) {
              return a
            }
          },
          null
        )
    )
}

export function encrypt (obj) {
  const toEncode = JSON.stringify(obj)
  return listKeys()
    .then(([{ key, uri }]) => {
      const iv = forge.random.getBytes(IV_LENGTH)
      const input = forge.util.createBuffer(toEncode)
      const cipher = forge.aes.startEncrypting(key, iv)
      cipher.update(input)
      const status = cipher.finish()

      if (status === false) {
        throw new Error('Encryption error')
      }

      return base64encode(iv + cipher.output.data)
    })
    .then(encoded => decrypt(encoded, false).then(decoded => {
      if (!isEqual(obj, decoded)) {
        throw new Error('Encryption test failed')
      }
      return encoded
    }))
}
